import { useMemo, useState } from "react";
import Input, { InputAsEnum, InputTypeEnum } from "src/components/common/Input";
import FormRowVertical from "src/components/common/FormRowVertical";
import { PlatformLocale } from "src/hooks/useStorePosLocales";
import { LocaleTranslationMap, localeLabel } from "src/utils/localeTranslations";

type MenuItemLocaleFieldsProps = {
  enabledLocaleOptions: PlatformLocale[];
  defaultLocale: string;
  localeTranslations: LocaleTranslationMap;
  onChange: (next: LocaleTranslationMap) => void;
  nameError?: string;
  entityLabel?: string;
  showDescription?: boolean;
};

const MenuItemLocaleFields = ({
  enabledLocaleOptions,
  defaultLocale,
  localeTranslations,
  onChange,
  nameError,
  entityLabel = "Item",
  showDescription = true,
}: MenuItemLocaleFieldsProps) => {
  const initialTab = enabledLocaleOptions.some((locale) => locale.code === defaultLocale)
    ? defaultLocale
    : enabledLocaleOptions[0]?.code || "en-US";
  const [activeLocale, setActiveLocale] = useState(initialTab);

  const activeOption = useMemo(
    () => enabledLocaleOptions.find((locale) => locale.code === activeLocale) ?? enabledLocaleOptions[0],
    [activeLocale, enabledLocaleOptions]
  );

  const updateLocaleField = (localeCode: string, field: "name" | "description", value: string) => {
    onChange({
      ...localeTranslations,
      [localeCode]: {
        name: localeTranslations[localeCode]?.name ?? "",
        description: localeTranslations[localeCode]?.description ?? "",
        [field]: value,
      },
    });
  };

  if (enabledLocaleOptions.length === 0) {
    return null;
  }

  const activeValues = localeTranslations[activeOption.code] ?? { name: "", description: "" };
  const showTabs = enabledLocaleOptions.length > 1;

  return (
    <div className="space-y-3">
      {showTabs ? (
        <div className="flex flex-wrap gap-2">
          {enabledLocaleOptions.map((locale) => {
            const selected = locale.code === activeOption.code;
            return (
              <button
                key={locale.code}
                type="button"
                onClick={() => setActiveLocale(locale.code)}
                className={`rounded-lg border px-3 py-1.5 text-sm transition-colors ${
                  selected
                    ? "border-[#FD7657]/30 bg-[#fef3ef] text-[#111827]"
                    : "border-[#E5E7EB] bg-white text-[#6b7280] hover:bg-[#fafafa]"
                }`}
              >
                {locale.nativeLabel}
                {locale.code === defaultLocale ? (
                  <span className="ml-1 text-xs text-[#FD7657]">Default</span>
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}

      <FormRowVertical
        label={`${entityLabel} name (${localeLabel(activeOption.code, activeOption.nativeLabel, activeOption.label)})`}
        required={activeOption.code === defaultLocale}
        error={activeOption.code === defaultLocale ? nameError : undefined}
        compact
      >
        <Input
          name={`locale-name-${activeOption.code}`}
          type={InputTypeEnum.TEXT}
          placeholder={`Input ${entityLabel.toLowerCase()} name`}
          value={activeValues.name}
          onChange={(event) => updateLocaleField(activeOption.code, "name", event.target.value)}
        />
      </FormRowVertical>

      {showDescription ? (
        <FormRowVertical
          label={`Description (${localeLabel(activeOption.code, activeOption.nativeLabel, activeOption.label)})`}
          optional
          compact
        >
          <Input
            name={`locale-description-${activeOption.code}`}
            as={InputAsEnum.TEXTAREA}
            rows={2}
            placeholder={`Write a description of the ${entityLabel.toLowerCase()}`}
            value={activeValues.description}
            onChange={(event) => updateLocaleField(activeOption.code, "description", event.target.value)}
          />
        </FormRowVertical>
      ) : null}
    </div>
  );
};

export default MenuItemLocaleFields;
